import { memo } from "react";

import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";

import ClinicalCrmContextInfoBanner from "../../messageTemplates/components/ClinicalCrmContextInfoBanner";
import MessageTemplatePhonePreviewContent from "../../messageTemplates/components/MessageTemplatePhonePreviewContent";
import type { MessageTemplate } from "../../messageTemplates/types";
import {
  formatTemplateDisplayName,
  getStatusUi,
  templateStatusChipSx,
} from "../../messageTemplates/utils/messageTemplateUiUtils";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import AutomationLinkedQuestionnairePreview from "./AutomationLinkedQuestionnairePreview";

export interface AutomationMessageTemplatePreviewDialogProps {
  open: boolean;
  template: MessageTemplate | null;
  organizationId: string;
  questionnaire?: QuestionnairePayload | null;
  onClose: () => void;
}

const AutomationMessageTemplatePreviewDialog = memo(
  ({
    open,
    template,
    organizationId,
    questionnaire,
    onClose,
  }: AutomationMessageTemplatePreviewDialogProps) => {
    const displayName = template
      ? formatTemplateDisplayName(template.name, {
          organizationPrefix: organizationId,
        })
      : "";
    const statusUi = template ? getStatusUi(template.status) : null;

    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {displayName || "Modelo de mensagem"}
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {template && statusUi ? (
            <Stack spacing={1.5}>
              <Chip
                label={statusUi.label}
                color={statusUi.color}
                size="small"
                variant="outlined"
                sx={{ alignSelf: "flex-start", ...templateStatusChipSx }}
              />

              <MessageTemplatePhonePreviewContent
                isCard={false}
                header={template.header}
                body={template.preview}
                hasButton={Boolean(
                  template.buttonType && String(template.buttonText ?? "").trim(),
                )}
                buttonText={template.buttonText ?? ""}
                buttonType={template.buttonType ?? ""}
                footerTime={dayjs(template.createdAtIso).format("HH:mm")}
              />

              {questionnaire ? (
                <AutomationLinkedQuestionnairePreview questionnaire={questionnaire} />
              ) : (
                <ClinicalCrmContextInfoBanner
                  variant="info"
                  title="Sem questionário vinculado"
                  icon={<InfoOutlinedIcon />}
                  description="Este modelo não possui questionário vinculado."
                />
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

AutomationMessageTemplatePreviewDialog.displayName =
  "AutomationMessageTemplatePreviewDialog";

export default AutomationMessageTemplatePreviewDialog;
